// React and Router
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom"

// Firebase
import "firebase/auth"
import { auth, logout, db } from "../../firebase.js"

// Context
import { useQuickLinks } from "../../contexts/QuickLinksContext";

// Styles
import "../../css/Header.css"
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp, faChevronDown, faBell, faEllipsisVertical } from '@fortawesome/free-solid-svg-icons';
import { appendSSOCode, warmSSOCode } from '../../utils/sso';

function Header() {

        const navigate = useNavigate();
        const { notificationPreferences } = useQuickLinks();
        const [menuOpen, setMenuOpen] = useState(false)
        const [loggedIn, setLoggedIn ] = useState(false)
        const [currentUserUid, setCurrentUserUid] = useState("")
        const [accountDropdownOpen, setAccountDropdownOpen] = useState(false)
        const [documentationDropdownOpen, setDocumentationDropdownOpen] = useState(false)
        const [notificationDropdownOpen, setNotificationDropdownOpen] = useState(false)
        const [suiteDropdownOpen, setSuiteDropdownOpen] = useState(false)
        const [notifications, setNotifications] = useState([])
        const [unreadCount, setUnreadCount] = useState(0)
        // Bump to re-render once an on-demand SSO code lands, so the suite links
        // (built synchronously via appendSSOCode) pick it up before the click.
        const [, setSsoTick] = useState(0)

        useEffect(() => {
                const unsubscribe = auth.onAuthStateChanged((user) => {
                        if (user) {
                                setLoggedIn(true)
                                setCurrentUserUid(user.uid)
                        } else {
                                setLoggedIn(false)
                                setCurrentUserUid("")
                                setNotifications([])
                                setUnreadCount(0)
                        }
                })

                return () => unsubscribe()
        }, [])

        // Fetch notifications when user is logged in. Polls only while the tab is visible —
        // hidden tabs used to keep polling, which alone kept the serverless database from
        // ever scaling to zero. A visibilitychange refetch keeps the badge fresh on return.
        useEffect(() => {
                if (loggedIn && currentUserUid) {
                        fetchNotifications();
                        const tick = () => { if (!document.hidden) fetchNotifications(); };
                        const interval = setInterval(tick, 120000);
                        const onVisibilityChange = () => { if (!document.hidden) fetchNotifications(); };
                        document.addEventListener('visibilitychange', onVisibilityChange);
                        return () => {
                                clearInterval(interval);
                                document.removeEventListener('visibilitychange', onVisibilityChange);
                        };
                }
        }, [loggedIn, currentUserUid, notificationPreferences])

        const handleLogout = () => { logout() }

        const toggleMenu = () => { setMenuOpen(!menuOpen) }
        
        const toggleAccountDropdown = (e) => { 
                e.stopPropagation();
                setAccountDropdownOpen(!accountDropdownOpen) 
        }

        const toggleDocumentationDropdown = (e) => {
                e.stopPropagation();
                setDocumentationDropdownOpen(!documentationDropdownOpen)
        }

        const toggleNotificationDropdown = (e) => {
                e.stopPropagation();
                setNotificationDropdownOpen(!notificationDropdownOpen)
        }

        const toggleSuiteDropdown = (e) => {
                e.stopPropagation();
                const opening = !suiteDropdownOpen
                setSuiteDropdownOpen(opening)
                if (opening && loggedIn) {
                        // The menu holds SSO'd links to the other suite apps — issue a
                        // code now (BBD-50), while the user decides which one to click.
                        warmSSOCode().then(() => setSsoTick(t => t + 1))
                }
        }

        // Filter notifications based on user preferences
        const filterNotifications = (notificationsList) => {
                return notificationsList.filter(notification => {
                        const type = notification.notification_type;
                        
                        // Map notification types to preference keys
                        switch(type) {
                                case 'comment':
                                        return notificationPreferences.commentNotifications;
                                case 'document_created':
                                case 'document_shared':
                                case 'document_updated':
                                        return notificationPreferences.documentNotifications;
                                case 'user_subscription':
                                case 'workspace_subscription':
                                        return notificationPreferences.subscriptionNotifications;
                                case 'workspace_management':
                                        return notificationPreferences.workspaceNotifications;
                                case 'mention':
                                        return notificationPreferences.mentionNotifications;
                                case 'reminder':
                                        return notificationPreferences.reminderNotifications;
                                case 'export_ready':
                                case 'export_failed':
                                        return notificationPreferences.exportNotifications ?? true;
                                default:
                                        return true; // Show unknown notification types by default
                        }
                });
        };

        const fetchNotifications = async () => {
                if (!currentUserUid) return;
                
                try {
                        const user = auth.currentUser;
                        if (!user) return;
                        
                        const token = await user.getIdToken();
                        
                        const response = await fetch("https://bluebirddocumentationadmin.pythonanywhere.com/notifications/all/", {
                                method: 'GET',
                                headers: {
                                        'Content-Type': 'application/json',
                                        'auth-token': `Bearer ${token}`,
                                        'Authorization': `${token}`,
                                        'userUid': currentUserUid
                                }
                        });
                        
                        if (response.ok) {
                                const data = await response.json();
                                const allNotifications = data.notifications || [];
                                
                                // Filter notifications based on user preferences
                                const filteredNotifications = filterNotifications(allNotifications);
                                
                                setNotifications(filteredNotifications);
                                const unread = filteredNotifications.filter(n => !n.is_read).length || 0;
                                setUnreadCount(unread);
                        }
                } catch (error) {
                        console.error('Error fetching notifications:', error);
                }
        }

        const clearAllNotifications = async () => {
                try {
                        const user = auth.currentUser;
                        if (!user) return;

                        const token = await user.getIdToken();

                        const response = await fetch("https://bluebirddocumentationadmin.pythonanywhere.com/notifications/delete-all/", {
                                method: 'DELETE',
                                headers: {
                                        'Content-Type': 'application/json',
                                        'auth-token': `Bearer ${token}`,
                                        'Authorization': `${token}`,
                                        'userUid': currentUserUid
                                }
                        });

                        if (response.ok) {
                                setNotifications([]);
                                setUnreadCount(0);
                        }
                } catch (error) {
                        console.error('Error clearing all notifications:', error);
                }
        }

        const deleteNotification = async (notificationId) => {
                try {
                        const user = auth.currentUser;
                        if (!user) return;
                        
                        const token = await user.getIdToken();
                        
                        const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/notifications/${notificationId}/delete/`, {
                                method: 'DELETE',
                                headers: {
                                        'Content-Type': 'application/json',
                                        'auth-token': `Bearer ${token}`,
                                        'Authorization': `${token}`
                                }
                        });
                        
                        if (response.ok) {
                                // Remove notification from local state
                                setNotifications(prev => prev.filter(n => n.id !== notificationId));
                                // Update unread count
                                setUnreadCount(prev => {
                                        const deletedNotification = notifications.find(n => n.id === notificationId);
                                        return deletedNotification && !deletedNotification.is_read ? prev - 1 : prev;
                                });
                        }
                } catch (error) {
                        console.error('Error deleting notification:', error);
                }
        }

        const formatTimeAgo = (dateString) => {
                const now = new Date();
                const date = new Date(dateString);
                const diffInSeconds = Math.floor((now - date) / 1000);
                
                if (diffInSeconds < 60) return 'Just now';
                if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
                if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
                return `${Math.floor(diffInSeconds / 86400)}d ago`;
        }

        const handleNotificationClick = (notification, event) => {
                if (notification.related_url) {
                        // Convert the API path to the correct frontend route
                        let url = notification.related_url;
                        
                        // Replace /spaces/ with /documentation/space/ to match frontend routing
                        if (url.startsWith('/spaces/')) {
                                url = url.replace('/spaces/', '/documentation/space/');
                        }

                        if (url.startsWith('/pages/')) {
                                url = url.replace('/pages/', '/documentation/page/');
                        }

                        // Handle audio transcription job URLs
                        // Backend sends: /audio/transcribe/jobs/:jobId/ or similar
                        // Frontend route: /audio-transcription/create-document/:jobId
                        const audioJobMatch = url.match(/\/audio\/transcribe\/jobs\/(\d+)\/?/);
                        if (audioJobMatch) {
                                url = `/audio-transcription/create-document/${audioJobMatch[1]}`;
                        } else if (url.startsWith('/audio/transcribe') || url.includes('/audio/transcribe')) {
                                // Generic audio transcription page
                                url = '/audio-transcription';
                        }

                        // Ensure URL starts with / for relative paths
                        if (!url.startsWith('/') && !url.startsWith('http')) {
                                url = '/' + url;
                        }

                        // Build the full URL with base domain
                        const baseUrl = 'https://bluebird-documentation.com';
                        const fullUrl = url.startsWith('http') ? url : `${baseUrl}${url}`;
                        
                        // Close the dropdown
                        setNotificationDropdownOpen(false);
                        
                        // Check if middle mouse button (button 1) or ctrl+click for new tab
                        if (event.button === 1 || event.ctrlKey || event.metaKey) {
                                // Open in new tab
                                window.open(fullUrl, '_blank');
                        } else {
                                // For same-tab navigation, use navigate for internal routes
                                // to avoid full page reload
                                navigate(url);
                        }
                }
        }                // Close dropdown when clicking outside
        useEffect(() => {
                const handleClickOutside = () => {
                        setAccountDropdownOpen(false)
                        setDocumentationDropdownOpen(false)
                        setNotificationDropdownOpen(false)
                        setSuiteDropdownOpen(false)
                }

                if (accountDropdownOpen || documentationDropdownOpen || notificationDropdownOpen || suiteDropdownOpen) {
                        document.addEventListener('click', handleClickOutside)
                }

                return () => {
                        document.removeEventListener('click', handleClickOutside)
                }
        }, [accountDropdownOpen, documentationDropdownOpen, notificationDropdownOpen, suiteDropdownOpen])

        return (
                <div className="Header__navbar">

                        <div className="Header__navbar-logo">
                                <Link to="/">
                                        <img 
                                                style={{ marginBottom: "0px" }}
                                                src={"https://i.ibb.co/1GqfVv1/Blue-Bird-Logo-Color-Light-Text.png"}
                                                alt="Logo"
                                                width={130}
                                                height={100}
                                        />
                                </Link>
                        </div>

                        <div className={`Header__navbar-buttons ${menuOpen ? "open" : ""}`}>                        
                            {loggedIn ? (
                                <>
                                                {/* Notification Center */}
                                                <div className="Header__dropdown-container">
                                                        <button 
                                                                type="button" 
                                                                className="Header__notification-button"
                                                                onClick={toggleNotificationDropdown}
                                                        >
                                                                <FontAwesomeIcon icon={faBell} />
                                                                {unreadCount > 0 && (
                                                                        <span className="Header__notification-badge">
                                                                                {unreadCount}
                                                                        </span>
                                                                )}
                                                        </button>
                                                        {notificationDropdownOpen && (
                                                                <div className="Header__dropdown-menu Header__notification-dropdown">
                                                                        <div className="Header__notification-header">
                                                                                <h3>Notifications</h3>
                                                                                {notifications.length > 0 && (
                                                                                        <div className="Header__notification-header-actions">
                                                                                                <span className="Header__notification-count">
                                                                                                        {notifications.length} total
                                                                                                </span>
                                                                                                <button
                                                                                                        className="Header__notification-clear-all"
                                                                                                        onClick={(e) => {
                                                                                                                e.stopPropagation();
                                                                                                                clearAllNotifications();
                                                                                                        }}
                                                                                                        title="Clear all notifications"
                                                                                                >
                                                                                                        Clear all
                                                                                                </button>
                                                                                        </div>
                                                                                )}
                                                                        </div>
                                                                        <div className="Header__notification-list">
                                                                                {notifications.length === 0 ? (
                                                                                        <div className="Header__notification-empty">
                                                                                                No notifications
                                                                                        </div>
                                                                                ) : (
                                                                                        notifications.slice(0, 10).map((notification) => (
                                                                                                <div 
                                                                                                        key={notification.id} 
                                                                                                        className={`Header__notification-item ${!notification.is_read ? 'unread' : ''} ${notification.related_url ? 'clickable' : ''}`}
                                                                                                        onClick={(e) => handleNotificationClick(notification, e)}
                                                                                                        onMouseDown={(e) => {
                                                                                                                // Handle middle mouse button
                                                                                                                if (e.button === 1) {
                                                                                                                        e.preventDefault();
                                                                                                                        handleNotificationClick(notification, e);
                                                                                                                }
                                                                                                        }}
                                                                                                        style={{ cursor: notification.related_url ? 'pointer' : 'default' }}
                                                                                                >
                                                                                                        <div className="Header__notification-content">
                                                                                                                <div className="Header__notification-title">
                                                                                                                        {notification.title}
                                                                                                                </div>
                                                                                                                <div className="Header__notification-message">
                                                                                                                        {notification.message}
                                                                                                                </div>
                                                                                                                <div className="Header__notification-meta">
                                                                                                                        <span className="Header__notification-time">
                                                                                                                                {formatTimeAgo(notification.created_at)}
                                                                                                                        </span>
                                                                                                                        <span className="Header__notification-type">
                                                                                                                                {notification.notification_type}
                                                                                                                        </span>
                                                                                                                </div>
                                                                                                        </div>
                                                                                                        <button 
                                                                                                                className="Header__notification-delete"
                                                                                                                onClick={(e) => {
                                                                                                                        e.stopPropagation(); // Prevent notification click when deleting
                                                                                                                        deleteNotification(notification.id);
                                                                                                                }}
                                                                                                                title="Delete notification"
                                                                                                        >
                                                                                                                ×
                                                                                                        </button>
                                                                                                </div>
                                                                                        ))
                                                                                )}
                                                                        </div>
                                                                        {notifications.length > 10 && (
                                                                                <div className="Header__notification-footer">
                                                                                        <button className="Header__notification-view-all">
                                                                                                View all notifications
                                                                                        </button>
                                                                                </div>
                                                                        )}
                                                                </div>
                                                        )}
                                                </div>
                                        <div className="Header__dropdown-container">
                                                <button 
                                                        type="button" 
                                                        className="button Header__buttons Header__dropdown-toggle"
                                                        onClick={toggleDocumentationDropdown}
                                                >
                                                    Documentation{' '}
                                                    <FontAwesomeIcon 
                                                        icon={documentationDropdownOpen ? faChevronUp : faChevronDown}
                                                    />
                                                </button>
                                                {documentationDropdownOpen && (
                                                        <div className="Header__dropdown-menu">
                                                                <Link to="/documentation">
                                                                        <button type="button" className="Header__dropdown-item">
                                                                                Browse Docs
                                                                        </button>
                                                                </Link>
                                                                <Link to="/create">
                                                                        <button type="button" className="Header__dropdown-item">
                                                                                Create New
                                                                        </button>
                                                                </Link>
                                                                <Link to="/templates">
                                                                        <button type="button" className="Header__dropdown-item">
                                                                                Templates
                                                                        </button>
                                                                </Link>
                                                                <div className="Header__dropdown-divider"></div>
                                                                <Link to="/walkthroughs">
                                                                        <button type="button" className="Header__dropdown-item">
                                                                                Walkthroughs
                                                                        </button>
                                                                </Link>
                                                                <div className="Header__dropdown-divider"></div>
                                                                <Link to="/import-document">
                                                                        <button type="button" className="Header__dropdown-item">
                                                                            Import Document
                                                                        </button>
                                                                </Link>
                                                                <Link to="/pull-from-writer">
                                                                        <button type="button" className="Header__dropdown-item">
                                                                            Pull from Writer
                                                                        </button>
                                                                </Link>
                                                                <Link to="/import-space">
                                                                        <button type="button" className="Header__dropdown-item">
                                                                            Import Space
                                                                        </button>
                                                                </Link>
                                                                <Link to="/audio-transcription">
                                                                        <button type="button" className="Header__dropdown-item">
                                                                                <i className="fas fa-microphone Header__audio-icon"></i>
                                                                                Audio Transcription
                                                                        </button>
                                                                </Link>
                                                        </div>
                                                )}
                                        </div>
                                                <Link to="/my-notebook">
                                                        <button type="button" className="button Header__buttons">
                                                                My Notebook
                                                        </button>
                                                </Link>
                                                
                                                <div className="Header__dropdown-container">
                                                        <button 
                                                                type="button" 
                                                                className="button Header__buttons Header__dropdown-toggle"
                                                                onClick={toggleAccountDropdown}
                                                        >
                                                                Account {' '}
                                                                <FontAwesomeIcon 
                                                                    icon={accountDropdownOpen ? faChevronUp : faChevronDown}
                                                                />
                                                        </button>
                                                        {accountDropdownOpen && (
                                                                <div className="Header__dropdown-menu">
                                                                        <Link to={`/account/${currentUserUid}`}>
                                                                                <button type="button" className="Header__dropdown-item">
                                                                                        Settings
                                                                                </button>
                                                                        </Link>
                                                                        <Link to="/workspaces">
                                                                                <button type="button" className="Header__dropdown-item">
                                                                                        Workspaces
                                                                                </button>
                                                                        </Link>
                                                                        <Link to="/help">
                                                                            <button type="button" className="Header__dropdown-item">
                                                                                    Help
                                                                            </button>
                                                                        </Link>
                                                                </div>
                                                        )}
                                                </div>
                                                <button type="button" className="button logout Header__logout-button" onClick={handleLogout}>
                                                        Logout
                                                </button>

                                                {/* BB Suite */}
                                                <div className="Header__dropdown-container">
                                                        <button
                                                                type="button"
                                                                className="Header__suite-button"
                                                                onClick={toggleSuiteDropdown}
                                                                title="Bluebird Suite"
                                                        >
                                                                <FontAwesomeIcon icon={faEllipsisVertical} />
                                                        </button>
                                                        {suiteDropdownOpen && (
                                                                <div className="Header__dropdown-menu Header__suite-dropdown">
                                                                        <div className="Header__suite-header">Bluebird Suite</div>
                                                                        <a href="https://www.bluebird-teaching.com" target="_blank" rel="noopener noreferrer">
                                                                                <button type="button" className="Header__dropdown-item Header__suite-item Header__suite-item--teaching">
                                                                                        <span className="Header__suite-item-name">Bluebird Teaching</span>
                                                                                        <span className="Header__suite-item-desc">Learning &amp; Course Management</span>
                                                                                </button>
                                                                        </a>
                                                                        <a href={appendSSOCode("https://tasks.bluebird-documentation.com")} target="_blank" rel="noopener noreferrer">
                                                                                <button type="button" className="Header__dropdown-item Header__suite-item Header__suite-item--tasks">
                                                                                        <span className="Header__suite-item-name">Bluebird Tasks</span>
                                                                                        <span className="Header__suite-item-desc">Task &amp; Project Management</span>
                                                                                </button>
                                                                        </a>
                                                                        <a href={appendSSOCode("https://planner.bluebird-documentation.com")} target="_blank" rel="noopener noreferrer">
                                                                                <button type="button" className="Header__dropdown-item Header__suite-item Header__suite-item--planner">
                                                                                        <span className="Header__suite-item-name">Bluebird Planner</span>
                                                                                        <span className="Header__suite-item-desc">Calendar &amp; Planning</span>
                                                                                </button>
                                                                        </a>
                                                                        <a href={appendSSOCode("https://cc.bluebird-documentation.com")} target="_blank" rel="noopener noreferrer">
                                                                                <button type="button" className="Header__dropdown-item Header__suite-item Header__suite-item--command-center">
                                                                                        <span className="Header__suite-item-name">Bluebird Command Center</span>
                                                                                        <span className="Header__suite-item-desc">Settings, Files &amp; Storage</span>
                                                                                </button>
                                                                        </a>
                                                                        <a href="https://github.com/YomenT/bluebird-writer" target="_blank" rel="noopener noreferrer">
                                                                                <button type="button" className="Header__dropdown-item Header__suite-item Header__suite-item--writer">
                                                                                        <span className="Header__suite-item-name">Bluebird Writer</span>
                                                                                        <span className="Header__suite-item-desc">Offline Markdown Editor</span>
                                                                                </button>
                                                                        </a>
                                                                        <a href="https://github.com/YomenT/bluebird-focus" target="_blank" rel="noopener noreferrer">
                                                                                <button type="button" className="Header__dropdown-item Header__suite-item Header__suite-item--focus">
                                                                                        <span className="Header__suite-item-name">Bluebird Focus</span>
                                                                                        <span className="Header__suite-item-desc">Focus Timer</span>
                                                                                </button>
                                                                        </a>
                                                                        <Link to="/bluebird-games" onClick={() => setSuiteDropdownOpen(false)}>
                                                                                <button type="button" className="Header__dropdown-item Header__suite-item Header__suite-item--games">
                                                                                        <span className="Header__suite-item-name">Bluebird Games</span>
                                                                                        <span className="Header__suite-item-desc">Games by Bluebird</span>
                                                                                </button>
                                                                        </Link>
                                                                </div>
                                                        )}
                                                </div>
                                        </>
                                ) : (
                                        <>
                                                <Link to="/my-notebook">
                                                        <button type="button" className="button Header__buttons">
                                                                Try My Notebook
                                                        </button>
                                                </Link>
                                                <Link to="/documentation/search">
                                                    <button type="button" className="button Header__buttons">
                                                        Public Documentation
                                                    </button>
                                                </Link>
                                                <Link to="/help">
                                                    <button type="button" className="button Header__buttons">
                                                        Help
                                                    </button>
                                                </Link>
                                                <Link to="/login">
                                                        <button type="button" className="button login-button Header__login-button">
                                                                Login
                                                        </button>
                                                </Link>

                                                {/* BB Suite */}
                                                <div className="Header__dropdown-container">
                                                        <button
                                                                type="button"
                                                                className="Header__suite-button"
                                                                onClick={toggleSuiteDropdown}
                                                                title="Bluebird Suite"
                                                        >
                                                                <FontAwesomeIcon icon={faEllipsisVertical} />
                                                        </button>
                                                        {suiteDropdownOpen && (
                                                                <div className="Header__dropdown-menu Header__suite-dropdown">
                                                                        <div className="Header__suite-header">Bluebird Suite</div>
                                                                        <a href="https://www.bluebird-teaching.com" target="_blank" rel="noopener noreferrer">
                                                                                <button type="button" className="Header__dropdown-item Header__suite-item Header__suite-item--teaching">
                                                                                        <span className="Header__suite-item-name">Bluebird Teaching</span>
                                                                                        <span className="Header__suite-item-desc">Learning &amp; Course Management</span>
                                                                                </button>
                                                                        </a>
                                                                        <a href={appendSSOCode("https://tasks.bluebird-documentation.com")} target="_blank" rel="noopener noreferrer">
                                                                                <button type="button" className="Header__dropdown-item Header__suite-item Header__suite-item--tasks">
                                                                                        <span className="Header__suite-item-name">Bluebird Tasks</span>
                                                                                        <span className="Header__suite-item-desc">Task &amp; Project Management</span>
                                                                                </button>
                                                                        </a>
                                                                        <a href={appendSSOCode("https://planner.bluebird-documentation.com")} target="_blank" rel="noopener noreferrer">
                                                                                <button type="button" className="Header__dropdown-item Header__suite-item Header__suite-item--planner">
                                                                                        <span className="Header__suite-item-name">Bluebird Planner</span>
                                                                                        <span className="Header__suite-item-desc">Calendar &amp; Planning</span>
                                                                                </button>
                                                                        </a>
                                                                        <a href={appendSSOCode("https://cc.bluebird-documentation.com")} target="_blank" rel="noopener noreferrer">
                                                                                <button type="button" className="Header__dropdown-item Header__suite-item Header__suite-item--command-center">
                                                                                        <span className="Header__suite-item-name">Bluebird Command Center</span>
                                                                                        <span className="Header__suite-item-desc">Settings, Files &amp; Storage</span>
                                                                                </button>
                                                                        </a>
                                                                        <a href="https://github.com/YomenT/bluebird-writer" target="_blank" rel="noopener noreferrer">
                                                                                <button type="button" className="Header__dropdown-item Header__suite-item Header__suite-item--writer">
                                                                                        <span className="Header__suite-item-name">Bluebird Writer</span>
                                                                                        <span className="Header__suite-item-desc">Offline Markdown Editor</span>
                                                                                </button>
                                                                        </a>
                                                                        <a href="https://github.com/YomenT/bluebird-focus" target="_blank" rel="noopener noreferrer">
                                                                                <button type="button" className="Header__dropdown-item Header__suite-item Header__suite-item--focus">
                                                                                        <span className="Header__suite-item-name">Bluebird Focus</span>
                                                                                        <span className="Header__suite-item-desc">Focus Timer</span>
                                                                                </button>
                                                                        </a>
                                                                        <Link to="/bluebird-games" onClick={() => setSuiteDropdownOpen(false)}>
                                                                                <button type="button" className="Header__dropdown-item Header__suite-item Header__suite-item--games">
                                                                                        <span className="Header__suite-item-name">Bluebird Games</span>
                                                                                        <span className="Header__suite-item-desc">Games by Bluebird</span>
                                                                                </button>
                                                                        </Link>
                                                                </div>
                                                        )}
                                                </div>
                                        </>
                                )}
                        </div>

                        <div className="Header__navbar-menu-icon" onClick={toggleMenu}>
                                <i className="fa-solid fa-bars" style={{ color: "white", fontSize: "21px", zIndex: "1000" }}></i>
                        </div>

                </div>
        )
}

export default Header